import React, { useCallback, useMemo, useState } from 'react';
import { useApolloClient } from '@apollo/client';
import PropTypes from 'prop-types';
import { Button, FakeText, Typography } from '@totalsoft/rocket-ui';
import { useQuery, useMutation, useSubscription } from 'hooks/apollo';
import { CURRENCIES_DICTIONARY_QUERY } from 'features/common/queries';
import {
  ADD_UPDATE_INSURANCE_CONVENTION_DEDUCTIBLE_COST,
  DELETE_INSURANCE_CONVENTION_DEDUCTIBLE_COST
} from 'features/insuranceConventions/edit/mutations';
import { INSURANCE_CONVENTION_DAMAGE_TYPES, INSURANCE_CONVENTION_DEDUCTIBLE_COSTS } from 'features/insuranceConventions/edit/queries';
import {
  INSURANCE_CONVENTION_DEDUCTIBLE_COST_DELETED,
  INSURANCE_CONVENTION_DEDUCTIBLE_COST_SAVED
} from 'features/insuranceConventions/edit/subscriptions';
import { useTranslation } from 'react-i18next';
import { StyledTable, StyledHeaderCell, StyledGrayIcon } from 'components/common/Styles';
import { Grid, Box, Icon, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import InsuranceConventionDeductibleCostContainer from './InsuranceConvetionDeductibleCostContainer';
import useConfirmDialog from 'features/common/components/confirm/useConfirmDialog';
import { addCachedDetail, removeCachedDetail } from 'utils/apollo/cacheExtensionFunctions';
import { sortBy } from 'lodash';
import { useToast } from 'hooks/toasts/useToast';
import { usePermission } from 'providers/RightsHooks';
import { ADMIN_INSURANCE_CONVENTIONS_WRITE } from 'constants/rightsCodes';

const InsuranceConventionDeductibleCostsContainer = ({ productId }) => {
  const { t } = useTranslation();
  const addToast = useToast();
  const client = useApolloClient();
  const confirm = useConfirmDialog();
  const canWrite = usePermission(ADMIN_INSURANCE_CONVENTIONS_WRITE);

  const [deductibleCosts, setDeductibleCosts] = useState([]);

  const [inAdd, setInAdd] = useState(false);

  const isNewConvention = useMemo(() => !productId, [productId]);

  const { loading } = useQuery(INSURANCE_CONVENTION_DEDUCTIBLE_COSTS, {
    skip: !productId,
    variables: { productId },
    onCompleted: data => {
      const newDeductibleCosts = (data?.insuranceConventionDeductibleCosts || []).map(x => ({
        ...x,
        disabled: false,
        editing: false,
        isNew: false
      }));
      setDeductibleCosts(newDeductibleCosts);
    }
  });

  const { data: currenciesDictionaryData } = useQuery(CURRENCIES_DICTIONARY_QUERY);

  const { data: damageTypesData } = useQuery(INSURANCE_CONVENTION_DAMAGE_TYPES);

  const currencies = useMemo(() => currenciesDictionaryData?.currenciesDictionary || [], [currenciesDictionaryData]);

  const damageTypes = useMemo(() => damageTypesData?.damageTypes || [], [damageTypesData]);

  const handleAdd = useCallback(() => {
    setDeductibleCosts(prev => [{ isNew: true, key: crypto.randomUUID(), productId }, ...prev]);
    setInAdd(true);
  }, [productId]);

  const handleCancelAdd = useCallback(() => {
    setDeductibleCosts(prev => prev.filter((_, i) => i !== 0));
    setInAdd(false);
  }, []);

  const updateLocalDeductibleCost = useCallback(
    (deductibleCostId, property, value) => {
      setDeductibleCosts(prev => {
        const modifyDeductibleCost = deductibleCost => {
          const newDeductibleCost = { ...deductibleCost };
          newDeductibleCost[property] = value;
          if (property === 'editing' && !value) newDeductibleCost.disabled = false;
          return newDeductibleCost;
        };
        const newDeductibleCosts = prev.map(x =>
          x.productId === productId && x.deductibleCostId === deductibleCostId ? modifyDeductibleCost(x) : x
        );
        return newDeductibleCosts;
      });
    },
    [productId]
  );

  const [addOrUpdateInsuranceConventionDeductibleCost] = useMutation(ADD_UPDATE_INSURANCE_CONVENTION_DEDUCTIBLE_COST, {
    onError: ({ params }) => {
      updateLocalDeductibleCost(params?.deductibleCostId, 'disabled', false);
    }
  });

  const handleEditDeductibleCost = useCallback(
    deductibleCost => {
      updateLocalDeductibleCost(deductibleCost.deductibleCostId, 'disabled', true);

      // eslint-disable-next-line no-unused-vars
      const { isNew, key, editing, disabled, damageTypeName, currencyCode, ...sanitizedDeductibleCost } = deductibleCost;

      addOrUpdateInsuranceConventionDeductibleCost({
        variables: { input: sanitizedDeductibleCost },
        errorHandlingParams: { deductibleCostId: deductibleCost.deductibleCostId }
      });
    },
    [updateLocalDeductibleCost, addOrUpdateInsuranceConventionDeductibleCost]
  );

  const [deleteInsuranceConventionDeductibleCost] = useMutation(DELETE_INSURANCE_CONVENTION_DEDUCTIBLE_COST, {
    onError: ({ params }) => {
      updateLocalDeductibleCost(params.deductibleCostId, 'disabled', false);
    }
  });

  const handleDelete = useCallback(
    deductibleCostId => {
      confirm(
        () => {
          updateLocalDeductibleCost(deductibleCostId, 'disabled', true);
          deleteInsuranceConventionDeductibleCost({
            variables: { deductibleCostId },
            errorHandlingParams: { deductibleCostId }
          });
        },
        {
          title: t('InsuranceConventions.Details.DeductibleCosts.DeleteDeductible'),
          content: t('InsuranceConventions.Details.DeductibleCosts.AreYouSureDeleteDeductible')
        }
      );
    },
    [confirm, t, updateLocalDeductibleCost, deleteInsuranceConventionDeductibleCost]
  );

  useSubscription(INSURANCE_CONVENTION_DEDUCTIBLE_COST_SAVED, {
    onData: ({
      data: {
        data: {
          insuranceConventionDeductibleCostSaved: { isNew, deductibleCostId, deductibleCost }
        }
      }
    }) => {
      if (isNew) {
        handleCancelAdd();
        setDeductibleCosts(prev => {
          const newDeductibleCosts = [...prev, deductibleCost];
          sortBy(newDeductibleCosts, ['deductibleCostId']);
          return newDeductibleCosts;
        });
      } else {
        setDeductibleCosts(prev => {
          const newDeductibleCosts = prev.map(x =>
            x.deductibleCostId === deductibleCostId ? { ...deductibleCost, editing: false, disabled: false } : x
          );
          sortBy(newDeductibleCosts, ['deductibleCostId']);
          return newDeductibleCosts;
        });
      }
      addCachedDetail(
        client,
        { name: 'insuranceConventionDeductibleCosts', value: INSURANCE_CONVENTION_DEDUCTIBLE_COSTS },
        { productId },
        x => x.deductibleCostId === deductibleCostId,
        deductibleCost,
        ['deductibleCostId']
      );
      addToast(t('InsuranceConventions.Details.DeductibleCosts.DeductibleSaved'), 'success');
    }
  });

  useSubscription(INSURANCE_CONVENTION_DEDUCTIBLE_COST_DELETED, {
    onData: ({
      data: {
        data: {
          insuranceConventionDeductibleCostDeleted: { deductibleCostId }
        }
      }
    }) => {
      setDeductibleCosts(prev => prev.filter(x => !(x.deductibleCostId === deductibleCostId)));
      removeCachedDetail(
        client,
        { name: 'insuranceConventionDeductibleCosts', value: INSURANCE_CONVENTION_DEDUCTIBLE_COSTS },
        { productId },
        x => x.deductibleCostId === deductibleCostId,
        ['deductibleCostId']
      );
      addToast(t('InsuranceConventions.Details.DeductibleCosts.DeductibleDeleted'), 'success');
    }
  });

  const getDeductibleCostKey = useCallback(
    deductibleCost => (deductibleCost.isNew ? deductibleCost.key : `${deductibleCost.deductibleCostId}`),
    []
  );
  return (
    <>
      {isNewConvention ? (
        <Grid container flexDirection='column' gap={1} p={2}>
          <Box display='flex' gap={1} alignItems='center' justifyContent='center'>
            <Icon fontSize='small' color='primary'>
              error
            </Icon>
            <Typography color='textPrimary'>{t('InsuranceConventions.Details.Warning')}</Typography>
          </Box>
        </Grid>
      ) : (
        <StyledTable>
          <TableHead>
            <TableRow>
              <StyledHeaderCell>{t('InsuranceConventions.Details.DeductibleCosts.DamageType')}</StyledHeaderCell>
              <StyledHeaderCell>{t('InsuranceConventions.Details.DeductibleCosts.Percent')}</StyledHeaderCell>
              <StyledHeaderCell>{t('InsuranceConventions.Details.DeductibleCosts.Value')}</StyledHeaderCell>
              <StyledHeaderCell>{t('InsuranceConventions.Details.DeductibleCosts.Currency')}</StyledHeaderCell>
              <StyledHeaderCell align='right'>
                <Button
                  startIcon={<AddIcon />}
                  onClick={handleAdd}
                  disabled={inAdd || isNewConvention || !canWrite}
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  {t('Add')}
                </Button>
              </StyledHeaderCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              {!isNewConvention && loading && (
                <TableCell colSpan={5}>
                  <FakeText lines={5} />
                </TableCell>
              )}
              {!isNewConvention && !loading && deductibleCosts.length === 0 && (
                <TableCell colSpan={5}>
                  <Box display='flex' gap={1} alignItems='center' justifyContent='center'>
                    <StyledGrayIcon fontSize='small'>error</StyledGrayIcon>
                    <Typography color='textSecondary'>{t('InsuranceConventions.Details.AddValues')}</Typography>
                  </Box>
                </TableCell>
              )}
            </TableRow>
            {!isNewConvention &&
              !loading &&
              deductibleCosts.length > 0 &&
              deductibleCosts.map(deductibleCost => (
                <InsuranceConventionDeductibleCostContainer
                  key={getDeductibleCostKey(deductibleCost)}
                  deductibleCost={deductibleCost}
                  currencies={currencies}
                  damageTypes={damageTypes}
                  onAddOrUpdate={handleEditDeductibleCost}
                  onUpdateLocalDeductibleCost={updateLocalDeductibleCost}
                  onDelete={handleDelete}
                  onCancelAdd={handleCancelAdd}
                />
              ))}
          </TableBody>
        </StyledTable>
      )}
    </>
  );
};

InsuranceConventionDeductibleCostsContainer.propTypes = {
  productId: PropTypes.number
};

export default InsuranceConventionDeductibleCostsContainer;
